/**
 * BillingTab - Billing management tab for company settings.
 * Shows current plan, member count, cost, and subscription management buttons.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import billingService from '../../services/billingService';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const SectionCard = styled.div`
  background: ${(p) => p.theme.colors.bgSecondary};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: ${(p) => p.theme.borderRadius?.lg || '12px'};
  padding: 24px;
  margin-bottom: 24px;
  animation: ${fadeIn} 0.3s ease-out;
`;

const SectionTitle = styled.h2`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 16px 0;
`;

const PlanBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 14px;
  border-radius: ${(p) => p.theme.borderRadius?.full || '9999px'};
  font-size: 0.85rem;
  font-weight: 700;
  ${(p) =>
    p.$pro
      ? `
    background: linear-gradient(135deg, rgba(245,158,11,0.2), rgba(217,119,6,0.15));
    border: 1px solid #D97706;
    color: #F59E0B;
  `
      : `
    background: ${p.theme.colors.bgTertiary};
    border: 1px solid ${p.theme.colors.border};
    color: ${p.theme.colors.textSecondary};
  `}
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid ${(p) => p.theme.colors.border};

  &:last-child { border-bottom: none; }
`;

const InfoLabel = styled.span`
  font-size: 0.88rem;
  color: ${(p) => p.theme.colors.textSecondary};
`;

const InfoValue = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.text};
  font-family: ${(p) => p.theme.fonts?.mono};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  padding: 10px 24px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  background: linear-gradient(135deg, #F59E0B, #D97706);
  color: #fff;
  border: none;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  &:hover { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SecondaryButton = styled.button`
  padding: 10px 24px;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  background: transparent;
  border: 1px solid ${(p) => p.theme.colors.border};
  color: ${(p) => p.theme.colors.text};
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
  &:hover {
    border-color: ${(p) => p.theme.colors.primary};
    color: ${(p) => p.theme.colors.primary};
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const AIUsageSection = styled.div`
  margin-top: 8px;
`;

const UsageRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
`;

const UsageLabel = styled.span`
  font-size: 0.85rem;
  color: ${(p) => p.theme.colors.textSecondary};
`;

const UsageValue = styled.span`
  font-size: 0.88rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.text};
`;

const ErrorText = styled.div`
  color: ${(p) => p.theme.colors.error || '#EF4444'};
  font-size: 0.85rem;
  padding: 12px;
  text-align: center;
`;

const LoadingText = styled.div`
  color: ${(p) => p.theme.colors.textSecondary};
  font-size: 0.85rem;
  padding: 24px;
  text-align: center;
`;

function BillingTab({ slug }) {
  const { t } = useLanguage();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchBilling = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await billingService.getStatus(slug);
      setBilling(data);
    } catch (err) {
      setError(err.message || t('課金情報の取得に失敗しました', 'Failed to fetch billing info'));
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  const handleUpgrade = useCallback(async () => {
    setActionLoading(true);
    try {
      const data = await billingService.createCheckout(slug);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      setError(err.message || t('チェックアウトの作成に失敗しました', 'Failed to create checkout'));
    } finally {
      setActionLoading(false);
    }
  }, [slug, t]);

  const handleManage = useCallback(async () => {
    setActionLoading(true);
    try {
      const data = await billingService.createPortal(slug);
      if (data.portal_url) {
        window.location.href = data.portal_url;
      }
    } catch (err) {
      setError(err.message || t('ポータルの作成に失敗しました', 'Failed to create portal'));
    } finally {
      setActionLoading(false);
    }
  }, [slug, t]);

  if (loading) return <LoadingText>{t('読み込み中...', 'Loading...')}</LoadingText>;
  if (error && !billing) return <ErrorText>{error}</ErrorText>;
  if (!billing) return null;

  const isPro = billing.plan === 'pro';

  return (
    <>
      <SectionCard>
        <SectionTitle>{t('現在のプラン', 'Current Plan')}</SectionTitle>

        <div style={{ marginBottom: 20 }}>
          <PlanBadge $pro={isPro}>
            {isPro ? 'Pro' : 'Free'}
          </PlanBadge>
        </div>

        <InfoRow>
          <InfoLabel>{t('メンバー数', 'Members')}</InfoLabel>
          <InfoValue>{billing.active_members}{t('人', '')}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('無料枠', 'Free Limit')}</InfoLabel>
          <InfoValue>{billing.free_limit}{t('人まで', ' members')}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('課金対象', 'Billable Seats')}</InfoLabel>
          <InfoValue>{billing.billable_seats}{t('人', '')}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('月額料金', 'Monthly Cost')}</InfoLabel>
          <InfoValue>
            {isPro
              ? `${billing.billable_seats} x ¥${billing.price_per_seat} = ¥${billing.monthly_cost.toLocaleString()}`
              : '¥0'}
          </InfoValue>
        </InfoRow>
        {billing.current_period_end && (
          <InfoRow>
            <InfoLabel>{t('次回更新日', 'Next Renewal')}</InfoLabel>
            <InfoValue>
              {new Date(billing.current_period_end).toLocaleDateString()}
            </InfoValue>
          </InfoRow>
        )}

        <ButtonGroup>
          {!isPro && (
            <PrimaryButton onClick={handleUpgrade} disabled={actionLoading}>
              {actionLoading
                ? t('処理中...', 'Processing...')
                : t('プランをアップグレード', 'Upgrade Plan')}
            </PrimaryButton>
          )}
          {billing.has_stripe_subscription && (
            <SecondaryButton onClick={handleManage} disabled={actionLoading}>
              {t('サブスクリプション管理', 'Manage Subscription')}
            </SecondaryButton>
          )}
        </ButtonGroup>

        {error && <ErrorText style={{ marginTop: 12 }}>{error}</ErrorText>}
      </SectionCard>

      <SectionCard>
        <SectionTitle>{t('AI利用状況', 'AI Usage')}</SectionTitle>
        <AIUsageSection>
          <UsageRow>
            <UsageLabel>{t('今月のAIチャット', 'AI Chat (this month)')}</UsageLabel>
            <UsageValue>{billing.ai_usage.chat_this_month}{t('回', ' times')}</UsageValue>
          </UsageRow>
          <UsageRow>
            <UsageLabel>{t('今月の組織分析AI', 'Org Analysis AI (this month)')}</UsageLabel>
            <UsageValue>{billing.ai_usage.org_analysis_this_month}{t('回', ' times')}</UsageValue>
          </UsageRow>
        </AIUsageSection>
      </SectionCard>

      <SectionCard>
        <SectionTitle>{t('プラン比較', 'Plan Comparison')}</SectionTitle>
        <InfoRow>
          <InfoLabel>{t('メンバー数', 'Members')}</InfoLabel>
          <InfoValue>Free: {t('3人まで', 'Up to 3')} / Pro: {t('無制限', 'Unlimited')}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('AIチャット', 'AI Chat')}</InfoLabel>
          <InfoValue>Free: {t('3回/日', '3/day')} / Pro: {t('30回/日', '30/day')}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>{t('組織AI分析', 'Org AI Analysis')}</InfoLabel>
          <InfoValue>Free: -- / Pro: {t('利用可', 'Available')}</InfoValue>
        </InfoRow>
      </SectionCard>
    </>
  );
}

export default BillingTab;
